/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const AUTHORIZATION_USAGE_RECORD_DISCRIMINATOR = new Uint8Array([
  109, 102, 102, 17, 210, 84, 85, 210,
]);

export function getAuthorizationUsageRecordDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    AUTHORIZATION_USAGE_RECORD_DISCRIMINATOR,
  );
}

export type AuthorizationUsageRecord = {
  discriminator: ReadonlyUint8Array;
  /** Authorization this usage belongs to */
  authorization: Address;
  /** Agent address */
  agent: Address;
  /** Authorized source that used this */
  authorizedSource: Address;
  /** Index at time of use */
  usageIndex: bigint;
  /** Reputation change applied */
  reputationChange: bigint;
  /** Timestamp of usage */
  usedAt: bigint;
  /** Transaction signature (for cross-reference) */
  transactionSignature: string;
  /** Additional metadata (e.g., payment details) */
  metadata: Option<string>;
  /** PDA bump */
  bump: number;
};

export type AuthorizationUsageRecordArgs = {
  /** Authorization this usage belongs to */
  authorization: Address;
  /** Agent address */
  agent: Address;
  /** Authorized source that used this */
  authorizedSource: Address;
  /** Index at time of use */
  usageIndex: number | bigint;
  /** Reputation change applied */
  reputationChange: number | bigint;
  /** Timestamp of usage */
  usedAt: number | bigint;
  /** Transaction signature (for cross-reference) */
  transactionSignature: string;
  /** Additional metadata (e.g., payment details) */
  metadata: OptionOrNullable<string>;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link AuthorizationUsageRecordArgs} account data. */
export function getAuthorizationUsageRecordEncoder(): Encoder<AuthorizationUsageRecordArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["authorization", getAddressEncoder()],
      ["agent", getAddressEncoder()],
      ["authorizedSource", getAddressEncoder()],
      ["usageIndex", getU64Encoder()],
      ["reputationChange", getI64Encoder()],
      ["usedAt", getI64Encoder()],
      [
        "transactionSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      [
        "metadata",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: AUTHORIZATION_USAGE_RECORD_DISCRIMINATOR,
    }),
  );
}

/** Gets the decoder for {@link AuthorizationUsageRecord} account data. */
export function getAuthorizationUsageRecordDecoder(): Decoder<AuthorizationUsageRecord> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["authorization", getAddressDecoder()],
    ["agent", getAddressDecoder()],
    ["authorizedSource", getAddressDecoder()],
    ["usageIndex", getU64Decoder()],
    ["reputationChange", getI64Decoder()],
    ["usedAt", getI64Decoder()],
    [
      "transactionSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    [
      "metadata",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link AuthorizationUsageRecord} account data. */
export function getAuthorizationUsageRecordCodec(): Codec<
  AuthorizationUsageRecordArgs,
  AuthorizationUsageRecord
> {
  return combineCodec(
    getAuthorizationUsageRecordEncoder(),
    getAuthorizationUsageRecordDecoder(),
  );
}

export function decodeAuthorizationUsageRecord<
  TAddress extends string = string,
>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<AuthorizationUsageRecord, TAddress>;
export function decodeAuthorizationUsageRecord<
  TAddress extends string = string,
>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<AuthorizationUsageRecord, TAddress>;
export function decodeAuthorizationUsageRecord<
  TAddress extends string = string,
>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<AuthorizationUsageRecord, TAddress>
  | MaybeAccount<AuthorizationUsageRecord, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getAuthorizationUsageRecordDecoder(),
  );
}

export async function fetchAuthorizationUsageRecord<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<AuthorizationUsageRecord, TAddress>> {
  const maybeAccount = await fetchMaybeAuthorizationUsageRecord(
    rpc,
    address,
    config,
  );
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeAuthorizationUsageRecord<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<AuthorizationUsageRecord, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeAuthorizationUsageRecord(maybeAccount);
}

export async function fetchAllAuthorizationUsageRecord(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<AuthorizationUsageRecord>[]> {
  const maybeAccounts = await fetchAllMaybeAuthorizationUsageRecord(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeAuthorizationUsageRecord(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<AuthorizationUsageRecord>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeAuthorizationUsageRecord(maybeAccount),
  );
}
